import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ContentLoader from "react-content-loader";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { services } from "../../../api/merchant";
import { PrimaryButton } from "../../common/PrimaryButton";
import { convertToKebabCase } from "../../../utils/common";

const Services = () => {
  const sRowPerPage = 20;
  const [aListings, setListings] = useState([]);
  const [bLoading, setLoading] = useState(false);
  const [bLastPage, setLastPage] = useState(false);
  const [iPage, setPage] = useState(1);
  const [isSaving, setSaving] = useState(false);

  const [isUpdated, setIsUpdated] = useState("a");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    setLoading(false);
    setPage(1);
    setLastPage(false);
  }, [isUpdated]);

  useEffect(() => {
    handleListings();
  }, [isUpdated, iPage, bLoading]);

  const handleListings = async () => {
    if (bLoading || bLastPage) {
      return;
    }

    setLoading(true);

    const _requestData = {
      page_no: iPage,
      row_per_page: sRowPerPage,
      act: "list",
    };

    try {
      const response = await services(_requestData);
      if (response.error === 0) {
        if (
          response.data.pagination.current_page === 1 &&
          response.data.services_list.length === 0
        ) {
          setLastPage(true);
          setLoading(false);
          return;
        }

        setLoading(false);
        if (response.data.pagination.current_page === 1) {
          setListings(response.data.services_list);
          setPage(2);
        } else {
          const listings = aListings?.concat(response.data.services_list);
          setListings(listings);
          setPage(response.data.pagination.current_page + 1);
        }

        if (
          response.data.pagination.current_page ===
          response.data.pagination.total_page
        ) {
          setLastPage(true);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.log("Error:", error);
    }
  };

  const handleStatus = async (service_code, flag) => {
    setLoading(true);
    try {
      const _requestData = {
        act: flag,
        service_code,
      };
      const response = await services(_requestData);
      if (response.error === 0) {
        toast(response.msg);
        setIsUpdated((prev) => (prev === "a" ? "b" : "a"));
      } else {
        setLoading(false);
        toast(response.error_msg);
      }
    } catch (error) {
      setLoading(false);
      toast(error);
    }
  };

  const handleOnClose = () => {
    setListings([]);
    setLoading(false);
    setLastPage(false);
    setPage(1);
    setSaving(false);
  };

  const loaders = Array.from({ length: 10 }, (_, index) => (
    <ContentLoader
      key={index}
      speed={1}
      width="20rem"
      height="20rem"
      viewBox="0 0 195 205"
      backgroundColor="#f3f3f3"
      foregroundColor="#ecebeb"
    >
      <rect rx="5" ry="5" width="100%" height="100%" />
    </ContentLoader>
  ));

  return (
    <div>
      <div id="events" className="px-8">
        <div className="mb-5 flex flex-row justify-between">
          <div className="text-lg font-semibold">Event listings</div>
        </div>
        <div id="events" className="">
          <InfiniteScroll
            dataLength={aListings?.length || 0}
            next={() => handleListings()}
            hasMore={!bLastPage}
            loader={bLoading ? loaders : null}
            scrollThreshold={0.5}
            className={`${
              !bLoading && aListings?.length === 0
                ? ""
                : "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-8"
            }`}
          >
            {!bLoading && aListings?.length === 0 ? (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <div className="text-lg font-semibold mt-10">
                  Nothing is in your listing
                </div>
                <div className="text-sm">Your Listing is currently empty</div>
                <div className="w-60 mt-5">
                  <PrimaryButton
                    title={"Create Listing"}
                    onClick={() => {
                      navigate("/merchant/listing/new");
                    }}
                  />
                </div>
              </div>
            ) : (
              aListings?.map((data, i) => (
                <div
                  key={i}
                  className="relative justify-items-center text-center border rounded-lg"
                >
                  <div className="w-full h-60 relative overflow-hidden rounded-t-lg group">
                    <img
                      draggable="false"
                      onClick={() => {
                        navigate(
                          `/category/${data?.service_code}/${convertToKebabCase(
                            data?.service_name
                          )}/${data?.service_code}`
                        );
                      }}
                      alt={data.title}
                      src={data.cover_img}
                      className="w-full h-full object-cover  rounded-t-lg transition-transform duration-300 ease-in-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-t-lg pointer-events-none" />
                  </div>

                  <div className="p-4 flex flex-row w-full justify-between">
                    <div className="text-sm w-full">
                      <div className="font-primary-500 font-semibold">
                        {data.title}
                      </div>
                      <div className="text-xs">{data.service_name}</div>
                      <div className="text-xs">{data.location}</div>
                      {data?.status === "9" && (
                        <div className="text-xs text-sunrise-500 font-semibold">
                          For Approval
                        </div>
                      )}
                      <div className="flex w-full flex-row justify-between items-center mt-4">
                        <div
                          onClick={() => {
                            navigate(`/merchant/listing/${data.service_code}`);
                          }}
                          className="flex cursor-pointer rounded-full py-2 px-4 bg-primary-500"
                        >
                          <p className="text-xs text-white text-center">
                            Update
                          </p>
                        </div>

                        {/* Custom Switch with Status Text */}
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs ${
                              data?.status === "0"
                                ? "text-primary-500"
                                : "text-sunrise-500"
                            }`}
                          >
                            {data?.status === "0" ? "Enabled" : "Disabled"}
                          </span>

                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={data?.status === "0"}
                              onChange={() =>
                                handleStatus(
                                  data?.service_code,
                                  data?.status === "0"
                                    ? "deactivate"
                                    : "activate"
                                )
                              }
                              className="sr-only peer"
                            />
                            <div
                              className={`w-12 h-6 rounded-full transition-all duration-200
          ${data?.status === "0" ? "bg-primary-500" : "bg-sunrise-500"}`}
                            />
                            <div
                              className={`absolute w-4 h-4 bg-white rounded-full top-1 left-1
          peer-checked:translate-x-6 transition-all duration-200`}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
};

export default Services;
